import { useState, useMemo } from 'react';
import { useReportData } from '../hooks/useReportData';
import { ReportBarChart } from '../components/report/ReportBarChart';
import { ReportDrillDown } from '../components/report/ReportDrillDown';
import { ReportSection } from '../components/report/ReportSection';
import { KpiCard } from '../components/KpiCard';
import { SortableTable } from '../components/SortableTable';
import { fmtEur, fmtEur2, fmtPct } from '../components/report/formatters';
import { MARGIN_COLORS } from '../lib/chart-theme';
import type { ReportParams } from '../types/widget';

/**
 * Composant Marge brute pour le driver `alia-categorization`.
 *
 * Différences avec MargeBrute (driver `aleo-emis`) :
 *   - Axe principal = `regime` (sous-catégorie PROD), pas `client_code`.
 *   - Barres signées : achats en négatif (rouge), ventes en positif (vert) sur l'histo global.
 *   - Drill-down avec champ `tiers` fusionné (supplier/customer selon sens), pas de
 *     `customer_code`/`site_code` ALEO (driver ALIA n'enrichit pas ces dimensions).
 *   - Ordre canonique des catégories figé (cf. demande client) ; les catégories
 *     non listées passent en fin de tri, par ordre alphabétique.
 *   - Pas de bandeau `tiersFilterActive` : le filtre « Client (tiers) » est masqué pour
 *     ce driver (`filtre_client_aleo: false`).
 */

interface TableauRow {
    regime: string;
    montant_achat: number;
    montant_vente: number;
    marge_brute: number;
    taux_marge: number;
}

interface ParClientRow {
    mois: string;
    regime: string;
    marge_brute_mois: number;
    marge_brute_ytd: number;
}

interface ParCategorieRow {
    mois: string;
    regime: string;
    montant_achat: number;
    montant_vente: number;
    marge_brute_mois: number;
    marge_brute_ytd: number;
}

const DASH = '—';

/** Ordre canonique demandé par le client (UPPERCASE car les labels DW le sont). */
const REGIME_ORDER = [
    'CONSOMMABLES',
    'STOCK',
    'PRESTATION ALIA',
    'PRESTATION DE SERVICE',
    'STOCK AURAR',
    'ACHAT REVENTE',
];

/** Affichage Title-Case des labels ALIA. */
function displayRegime(regime: string | null | undefined): string {
    if (!regime) return DASH;
    return regime
        .toLowerCase()
        .replace(/(^|\s)\S/g, (m) => m.toUpperCase());
}

/** Comparator par ordre canonique puis alphabétique pour les inconnus.
 *
 * Tolère null/undefined : pendant les transitions de connector côté ConnectorPage,
 * le composant peut être brièvement monté avec des données d'un autre driver
 * (où `regime` n'existe pas) avant que le parent ne re-aiguille le composant.
 */
function compareRegime(a: string | null | undefined, b: string | null | undefined): number {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    const ia = REGIME_ORDER.indexOf(a);
    const ib = REGIME_ORDER.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
}

/** Tableau colonnes. */
const TABLE_COLUMNS = [
    {
        key: 'regime',
        label: 'Catégorie',
        format: (v: unknown) => displayRegime(String(v ?? '')),
    },
    {
        key: 'montant_achat',
        label: 'Achats',
        align: 'right' as const,
        format: (v: unknown) => fmtEur(Number(v)),
        totalKey: 'montant_achat',
        formatTotal: (v: number) => fmtEur2(v),
    },
    {
        key: 'montant_vente',
        label: 'Ventes',
        align: 'right' as const,
        format: (v: unknown) => fmtEur(Number(v)),
        totalKey: 'montant_vente',
        formatTotal: (v: number) => fmtEur2(v),
    },
    {
        key: 'marge_brute',
        label: 'Marge brute',
        align: 'right' as const,
        format: (v: unknown) => fmtEur(Number(v)),
        totalKey: 'marge_brute',
        formatTotal: (v: number) => fmtEur2(v),
    },
    {
        key: 'taux_marge',
        label: 'Taux',
        align: 'right' as const,
        format: (v: unknown) => fmtPct(Number(v)),
    },
];

/** Colonnes du drill-down (sans dimensions ALEO, avec tiers fusionné). */
const DRILL_COLUMNS = [
    { key: 'date_ecriture', label: 'Date' },
    {
        key: 'libelle',
        label: 'Libellé',
        filterable: true,
        format: (v: unknown) => String(v ?? '').replace(' (label généré)', ''),
    },
    { key: 'compte', label: 'Compte' },
    {
        key: 'regime',
        label: 'Catégorie',
        format: (v: unknown) => displayRegime(String(v ?? '')),
    },
    {
        key: 'tiers',
        label: 'Tiers',
        filterable: true,
        format: (v: unknown) => (v ? String(v) : DASH),
    },
    {
        key: 'montant',
        label: 'Montant',
        align: 'right' as const,
        format: (v: unknown) => fmtEur2(Number(v)),
        totalKey: 'montant',
        formatTotal: (v: number) => fmtEur2(v),
    },
    {
        key: 'sens',
        label: 'Sens',
        format: (v: unknown, row: Record<string, unknown>) => {
            const label = String(v ?? '');
            const type = String(row.transaction_type ?? '');
            const amount = Number(row.montant ?? 0);
            // Acte normal : ACHAT+ (charge débitée) / VENTE− (produit crédité).
            // Avoir : signe inversé sur la même ligne business.
            const isAvoir = (type === 'ACHAT' && amount < 0) || (type === 'VENTE' && amount > 0);
            return isAvoir ? `${label} (avoir)` : label;
        },
    },
];

interface DrillContext {
    mois: string;
    source: 'global' | 'categorie';
}

export function MargeBruteAlia({ filters }: { filters: ReportParams }) {
    const [drill, setDrill] = useState<DrillContext | null>(null);

    const tableau = useReportData<TableauRow>('/api/emis/marge-brute/tableau', filters);
    const parClient = useReportData<ParClientRow>('/api/emis/marge-brute/par-client', filters);
    const parCategorie = useReportData<ParCategorieRow>('/api/emis/marge-brute/par-categorie', filters);

    // Tri du tableau par ordre canonique des catégories.
    const sortedTableau = useMemo(
        () => [...tableau.data].sort((a, b) => compareRegime(a.regime, b.regime)),
        [tableau.data],
    );

    const kpis = useMemo(() => {
        const rows = tableau.data;
        if (!rows.length) return null;
        const totalVente = rows.reduce((s, r) => s + Number(r.montant_vente), 0);
        const totalAchat = rows.reduce((s, r) => s + Number(r.montant_achat), 0);
        const totalMarge = rows.reduce((s, r) => s + Number(r.marge_brute), 0);
        const tauxMarge = totalVente !== 0 ? (totalMarge / totalVente) * 100 : 0;
        return { totalVente, totalAchat, totalMarge, tauxMarge, nbCategories: rows.length };
    }, [tableau.data]);

    // Histo global signé : achats négatifs (rouge), ventes positives (vert), agrégés par mois.
    const globalChart = useMemo(() => {
        const months = [...new Set(parCategorie.data.map((r) => r.mois))].sort();
        const achatByMonth = new Map<string, number>();
        const venteByMonth = new Map<string, number>();
        for (const r of parCategorie.data) {
            achatByMonth.set(r.mois, (achatByMonth.get(r.mois) ?? 0) + Number(r.montant_achat));
            venteByMonth.set(r.mois, (venteByMonth.get(r.mois) ?? 0) + Number(r.montant_vente));
        }
        return {
            months,
            series: [
                {
                    name: 'Achats',
                    // Négatif pour visualiser les achats sous l'axe.
                    data: months.map((m) => -(achatByMonth.get(m) ?? 0)),
                    color: '#dc2626',
                },
                {
                    name: 'Ventes',
                    data: months.map((m) => venteByMonth.get(m) ?? 0),
                    color: MARGIN_COLORS.cumulative,
                },
            ],
        };
    }, [parCategorie.data]);

    // Histo par catégorie empilé — une série par catégorie de la marge nette mensuelle,
    // dans l'ordre canonique. Tooltip avec total cumulé.
    const categorieChart = useMemo(() => {
        const months = [...new Set(parCategorie.data.map((r) => r.mois))].sort();
        const categories = [...new Set(parCategorie.data.map((r) => r.regime).filter(Boolean))]
            .sort(compareRegime);
        const byKey = new Map(parCategorie.data.map((r) => [`${r.regime}-${r.mois}`, r]));

        return {
            months,
            series: categories.map((cat) => ({
                name: displayRegime(cat),
                data: months.map((m) => Number(byKey.get(`${cat}-${m}`)?.marge_brute_mois ?? 0)),
                stack: 'categories',
            })),
        };
    }, [parCategorie.data]);

    // Histo cumul YTD par catégorie (réutilise parClient).
    const cumulChart = useMemo(() => {
        const months = [...new Set(parClient.data.map((r) => r.mois))].sort();
        const categories = [...new Set(parClient.data.map((r) => r.regime).filter(Boolean))].sort(compareRegime);
        const byKey = new Map(parClient.data.map((r) => [`${r.regime}-${r.mois}`, r]));
        return {
            months,
            series: categories.map((cat) => ({
                name: displayRegime(cat),
                data: months.map((m) => Number(byKey.get(`${cat}-${m}`)?.marge_brute_ytd ?? 0)),
            })),
        };
    }, [parClient.data]);

    const drillParams = useMemo(() => (drill ? { ...filters, mois: drill.mois } : filters), [filters, drill]);

    return (
        <div className="space-y-6">
            {/* KPI Strip */}
            {kpis && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KpiCard label="Marge brute totale" value={fmtEur(kpis.totalMarge)} loading={tableau.loading} />
                    <KpiCard label="Taux de marge" value={fmtPct(kpis.tauxMarge)} loading={tableau.loading} />
                    <KpiCard label="CA ventes" value={fmtEur(kpis.totalVente)} loading={tableau.loading} />
                    <KpiCard label="Catégories" value={kpis.nbCategories} loading={tableau.loading} />
                </div>
            )}
            {tableau.loading && !kpis && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map((i) => <KpiCard key={i} label="" value="" loading />)}
                </div>
            )}

            {/* Tableau marge brute par catégorie */}
            <ReportSection title="Marge brute par catégorie" loading={tableau.loading} error={tableau.error}>
                <SortableTable
                    columns={TABLE_COLUMNS}
                    data={sortedTableau as unknown as Record<string, unknown>[]}
                    totals={kpis ? {
                        montant_achat: kpis.totalAchat,
                        montant_vente: kpis.totalVente,
                        marge_brute: kpis.totalMarge,
                    } : undefined}
                />
            </ReportSection>

            {/* Histogramme global signé — achats vs ventes par mois */}
            <ReportSection title="Achats vs ventes — vue mensuelle signée" loading={parCategorie.loading} error={parCategorie.error}>
                <ReportBarChart
                    title="Achats (rouge, sous l'axe) — Ventes (vert, au-dessus)"
                    categories={globalChart.months}
                    series={globalChart.series}
                    height={350}
                    valueFormatter={fmtEur}
                    onBarClick={(_, mois) => setDrill({ mois, source: 'global' })}
                    tooltipShowStackTotal
                />
                {drill?.source === 'global' && (
                    <ReportDrillDown
                        title={`Détail des écritures — ${drill.mois}`}
                        endpoint="/api/emis/marge-brute/drill-down"
                        params={drillParams}
                        enabled={true}
                        onClose={() => setDrill(null)}
                        columns={DRILL_COLUMNS}
                    />
                )}
            </ReportSection>

            {/* Histogramme par catégorie empilé — marge nette par mois */}
            <ReportSection title="Marge brute mensuelle par catégorie (empilée)" loading={parCategorie.loading} error={parCategorie.error}>
                <ReportBarChart
                    title="Marge nette mensuelle — toutes catégories empilées"
                    categories={categorieChart.months}
                    series={categorieChart.series}
                    height={350}
                    valueFormatter={fmtEur}
                    onBarClick={(_, mois) => setDrill({ mois, source: 'categorie' })}
                    tooltipShowStackTotal
                />
                {drill?.source === 'categorie' && (
                    <ReportDrillDown
                        title={`Détail des écritures — ${drill.mois}`}
                        endpoint="/api/emis/marge-brute/drill-down"
                        params={drillParams}
                        enabled={true}
                        onClose={() => setDrill(null)}
                        columns={DRILL_COLUMNS}
                    />
                )}
            </ReportSection>

            {/* Cumul YTD par catégorie */}
            <ReportSection title="Marge brute cumulée (YTD) par catégorie" loading={parClient.loading} error={parClient.error}>
                <ReportBarChart
                    title="Cumul annuel — toutes catégories"
                    categories={cumulChart.months}
                    series={cumulChart.series}
                    height={300}
                    valueFormatter={fmtEur}
                    tooltipShowStackTotal
                />
            </ReportSection>
        </div>
    );
}
